"use client"

import { useEffect, useRef } from "react"

/**
 * Global error boundary for the main layout.
 * For hydration/DOM errors: auto-reset ONCE (not infinitely).
 * This re-renders the page which typically resolves the transient error.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const hasReset = useRef(false)

  useEffect(() => {
    console.warn("[Global Error Boundary] Caught:", error.message)

    const isHydrationError =
      error.message?.includes("removeChild") ||
      error.message?.includes("not a child") ||
      error.message?.includes("Hydration") ||
      error.message?.includes("#418") ||
      error.message?.includes("#310")

    // For hydration errors: reset once to recover without blanking the page
    if (isHydrationError && !hasReset.current) {
      hasReset.current = true
      reset()
    }
  }, [error, reset])

  // For non-hydration errors or if reset already tried, show retry UI
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] gap-4 p-8">
      <p className="text-sm text-gray-500">Something went wrong.</p>
      <button
        onClick={() => reset()}
        className="px-4 py-2 text-sm bg-black text-white rounded hover:bg-gray-800"
      >
        Try again
      </button>
    </div>
  )
}
